import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Layered flow graph — a compact knowledge-graph view for "A feeds B feeds C"
 * relationships (vendor → invoice charge → product line, source → model →
 * report…). Pure inline SVG, no chart library.
 *
 * - ``columns`` declare the layers left→right; nodes belong to a column.
 * - Edge thickness scales with ``value`` (sqrt), so dollars read at a glance.
 * - ``dashed`` edges mean "apportioned downstream" (e.g. a computed split the
 *   engine resolves later); ``muted`` edges/nodes render subdued (exclusions).
 * - Hovering or selecting a node keeps its neighborhood at full strength and
 *   fades the rest.
 *
 * The component is presentation-only: pass ``onNodeClick`` to drive filters or
 * drawers from the host page.
 */

export interface FlowGraphColumn {
  key: string;
  title: string;
}

export interface FlowGraphNode {
  id: string;
  column: string;
  label: string;
  /** Optional magnitude — shown under the label and used to order nodes. */
  value?: number | null;
  tone?: "default" | "muted" | "attention";
}

export interface FlowGraphLink {
  source: string;
  target: string;
  value?: number | null;
  /** Apportioned downstream (rendered dashed). */
  dashed?: boolean;
  muted?: boolean;
}

const NODE_H = 30;
const NODE_GAP = 10;
const COL_W = 210;
const COL_GAP = 130;
const PAD = 12;
const HEADER_H = 30;

function edgeWidth(value: number | null | undefined, max: number): number {
  if (!value || value <= 0 || max <= 0) return 1.25;
  return Math.max(1.25, 12 * Math.sqrt(value / max));
}

export function FlowGraph({
  columns,
  nodes,
  links,
  formatValue,
  onNodeClick,
  selectedId,
  className,
}: {
  columns: FlowGraphColumn[];
  nodes: FlowGraphNode[];
  links: FlowGraphLink[];
  formatValue?: (v: number) => string;
  onNodeClick?: (node: FlowGraphNode) => void;
  selectedId?: string | null;
  className?: string;
}) {
  const [hoverId, setHoverId] = useState<string | null>(null);
  const focusId = hoverId ?? selectedId ?? null;

  const layout = useMemo(() => {
    const colIndex = new Map(columns.map((c, i) => [c.key, i]));
    const byColumn = new Map<string, FlowGraphNode[]>();
    for (const n of nodes) {
      const list = byColumn.get(n.column) ?? [];
      list.push(n);
      byColumn.set(n.column, list);
    }
    // Biggest first within each column; sinks (muted/attention) at the bottom.
    for (const list of byColumn.values()) {
      list.sort((a, b) => {
        const sink = (n: FlowGraphNode) => (n.tone && n.tone !== "default" ? 1 : 0);
        if (sink(a) !== sink(b)) return sink(a) - sink(b);
        return (b.value ?? 0) - (a.value ?? 0);
      });
    }
    const pos = new Map<string, { x: number; y: number }>();
    let maxRows = 0;
    for (const [colKey, list] of byColumn) {
      const ci = colIndex.get(colKey) ?? 0;
      const x = PAD + ci * (COL_W + COL_GAP);
      list.forEach((n, ri) => {
        pos.set(n.id, { x, y: HEADER_H + PAD + ri * (NODE_H + NODE_GAP) });
      });
      maxRows = Math.max(maxRows, list.length);
    }
    const width = PAD * 2 + columns.length * COL_W + (columns.length - 1) * COL_GAP;
    const height = HEADER_H + PAD * 2 + maxRows * (NODE_H + NODE_GAP) - NODE_GAP;
    const maxLink = Math.max(0, ...links.map((l) => l.value ?? 0));
    return { pos, width, height: Math.max(height, 120), maxLink, colIndex };
  }, [columns, nodes, links]);

  const neighborhood = useMemo(() => {
    if (!focusId) return null;
    const keep = new Set([focusId]);
    for (const l of links) {
      if (l.source === focusId) keep.add(l.target);
      if (l.target === focusId) keep.add(l.source);
    }
    return keep;
  }, [focusId, links]);

  const dim = (id: string) => neighborhood != null && !neighborhood.has(id);
  const dimLink = (l: FlowGraphLink) =>
    neighborhood != null && !(neighborhood.has(l.source) && neighborhood.has(l.target));

  return (
    <div className={cn("overflow-x-auto", className)}>
      <svg
        width={layout.width}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        role="img"
        aria-label="Flow graph"
        className="max-w-none"
      >
        {columns.map((c, i) => (
          <text
            key={c.key}
            x={PAD + i * (COL_W + COL_GAP)}
            y={16}
            className="fill-muted-foreground text-[10px] font-medium uppercase tracking-wider"
          >
            {c.title}
          </text>
        ))}

        {links.map((l, i) => {
          const s = layout.pos.get(l.source);
          const t = layout.pos.get(l.target);
          if (!s || !t) return null;
          const x1 = s.x + COL_W;
          const y1 = s.y + NODE_H / 2;
          const x2 = t.x;
          const y2 = t.y + NODE_H / 2;
          const mx = (x1 + x2) / 2;
          return (
            <path
              key={`${l.source}→${l.target}-${i}`}
              d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
              fill="none"
              strokeWidth={edgeWidth(l.value, layout.maxLink)}
              strokeDasharray={l.dashed ? "6 4" : undefined}
              strokeLinecap="round"
              className={cn(
                "stroke-brand transition-opacity",
                l.muted && "stroke-muted-foreground",
                dimLink(l) ? "opacity-10" : "opacity-35",
              )}
            />
          );
        })}

        {nodes.map((n) => {
          const p = layout.pos.get(n.id);
          if (!p) return null;
          const dimmed = dim(n.id);
          const selected = n.id === selectedId;
          return (
            <g
              key={n.id}
              transform={`translate(${p.x}, ${p.y})`}
              className={cn(
                "transition-opacity",
                dimmed && "opacity-25",
                onNodeClick && "cursor-pointer",
              )}
              onMouseEnter={() => setHoverId(n.id)}
              onMouseLeave={() => setHoverId(null)}
              onClick={onNodeClick ? () => onNodeClick(n) : undefined}
            >
              <rect
                width={COL_W}
                height={NODE_H}
                rx={6}
                strokeWidth={selected ? 2 : 1}
                className={cn(
                  "fill-card stroke-border",
                  selected && "stroke-brand",
                  n.tone === "muted" && "fill-muted",
                  n.tone === "attention" && "stroke-warning",
                )}
              />
              <text
                x={8}
                y={n.value != null ? 13 : 19}
                className="fill-foreground text-[11px] font-medium"
              >
                {n.label.length > 30 ? `${n.label.slice(0, 29)}…` : n.label}
              </text>
              {n.value != null && (
                <text x={8} y={25} className="fill-muted-foreground text-[10px]">
                  {formatValue ? formatValue(n.value) : String(n.value)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
